import classnames from 'classnames';
import type { CSSProperties, ReactNode } from 'react';
import { useState } from 'react';
import styled from 'styled-components';

import { skeletonStyle } from 'style/utils';

const DEFAULT_BORDER_RADIUS = 'var(--half-unit)';

const Wrapper = styled.span`
  display: contents;
  &.loading {
    background-color: rgba(var(--c-rgb-white), 0.05);
    border-radius: var(--half-unit);
    pointer-events: none;
    ${skeletonStyle}
    &:empty:before {
      content: '\\00a0';
      display: inline-block;
      width: 10ch;
    }
    /** Hide any content inside the wrapper */
    color: transparent;
    > * {
      visibility: hidden;
    }
  }
`;

type Props = {
  children?: ReactNode;
  className?: string;
  loading?: boolean;
  circle?: boolean;
};

export const Skeleton = ({ children, className, loading, circle }: Props) => {
  const [wrapperRef, setWrapperRef] = useState<HTMLSpanElement | null>(null);

  const childElement = wrapperRef?.children?.[0] as HTMLElement | undefined;
  const hasChildText = childElement?.nodeType === Node.TEXT_NODE;
  let style: CSSProperties = {};
  const childStyle: Partial<CSSStyleDeclaration> =
    !childElement || hasChildText
      ? { display: 'inline', borderRadius: DEFAULT_BORDER_RADIUS }
      : getComputedStyle(childElement);

  let { borderRadius } = childStyle;
  if (circle) {
    borderRadius = '50%';
  } else if (borderRadius === '0px') {
    borderRadius = DEFAULT_BORDER_RADIUS;
  }

  // Handle child text nodes or elements with display: inline
  if (childStyle.display === 'inline') {
    style = {
      display: 'inline-block',
      width: 'fit-content',
      height: 'auto',
      borderRadius,
    };
  } else {
    style = {
      display: childStyle.display,
      borderRadius,
      width: childElement?.offsetWidth,
      height: childElement?.offsetHeight,
    };
  }

  return (
    <Wrapper
      role={loading ? 'status' : undefined}
      aria-busy={loading}
      aria-live={loading ? 'polite' : undefined}
      ref={setWrapperRef}
      style={loading ? style : undefined}
      className={classnames(className, { loading })}
    >
      {children}
    </Wrapper>
  );
};
